import {
  ArrowLeftOutlined,
  IdcardOutlined,
  RobotOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Row,
  Skeleton,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import type { UserDetail, UserTransaction } from "@/entities/user";
import { useUser, useUserTransactions } from "@/entities/user";
import { AdjustBalanceButton } from "@/features/adjust-balance";
import { BlockUserButton } from "@/features/block-user";
import { MarkServiceUserButton } from "@/features/mark-service-user";
import { ResetDemoButton } from "@/features/reset-demo";
import { errorMessage } from "@/shared/api";
import { ROUTES, SECTIONS } from "@/shared/config";
import { formatDateTime } from "@/shared/lib";
import {
  EmptyState,
  LoadMore,
  Money,
  PageHeader,
  SectionCard,
  StatusTag,
} from "@/shared/ui";

function RobotsCard({ user }: { user: UserDetail }) {
  const { t } = useTranslation();
  const columns: ColumnsType<NonNullable<UserDetail["robots"]>[number]> = [
    { title: t("users.robot"), dataIndex: "robot_name" },
    {
      title: t("users.purchasePrice"),
      key: "purchase_price",
      align: "right",
      render: (_, row) => <Money value={row.purchase_price} />,
    },
    {
      title: t("users.earnedTotal"),
      key: "earned_total",
      align: "right",
      render: (_, row) => <Money value={row.earned_total} sign="always" colored />,
    },
    {
      title: t("common.status"),
      key: "status",
      width: 150,
      render: (_, row) => <StatusTag status={row.status} />,
    },
    {
      title: t("users.purchasedAt"),
      key: "purchased_at",
      render: (_, row) => formatDateTime(row.purchased_at),
    },
  ];

  return (
    <SectionCard
      color={SECTIONS.robots.color}
      icon={<RobotOutlined aria-hidden />}
      title={t("users.robots")}
      bodyPadding={0}
    >
      <Table
        rowKey="id"
        pagination={false}
        columns={columns}
        dataSource={user.robots ?? []}
        scroll={{ x: 900 }}
        locale={{
          emptyText: <EmptyState icon={<RobotOutlined aria-hidden />} title={t("users.noRobots")} />,
        }}
      />
    </SectionCard>
  );
}

function TransactionsCard({ userId }: { userId: number }) {
  const { t } = useTranslation();
  const query = useUserTransactions(userId);
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<UserTransaction> = [
    {
      title: t("users.transactionType"),
      key: "type",
      render: (_, row) => t(`transactionType.${row.type}`, row.type),
    },
    {
      title: t("common.amount"),
      key: "amount",
      align: "right",
      render: (_, row) => <Money value={row.amount} sign="always" colored />,
    },
    {
      title: t("users.balanceAfter"),
      key: "balance_after",
      align: "right",
      render: (_, row) => <Money value={row.balance_after} />,
    },
    {
      title: t("common.comment"),
      dataIndex: "comment",
      render: (value: string | null) =>
        value ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: t("common.date"),
      key: "created_at",
      width: 190,
      render: (_, row) => (
        <Typography.Text type="secondary">{formatDateTime(row.created_at)}</Typography.Text>
      ),
    },
  ];

  return (
    <SectionCard
      color={SECTIONS.withdrawals.color}
      icon={<SwapOutlined aria-hidden />}
      title={t("users.transactions")}
      bodyPadding={0}
    >
      <Table
        rowKey="id"
        pagination={false}
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        scroll={{ x: 1000 }}
        locale={{
          emptyText: <EmptyState icon={<SwapOutlined aria-hidden />} title={t("users.noTransactions")} />,
        }}
      />
      <LoadMore
        hasMore={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => {
          void query.fetchNextPage();
        }}
      />
    </SectionCard>
  );
}

export function UserDetailPage() {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = Number(params.id);
  const { data, isPending, isError, error } = useUser(userId);

  if (isError) return <Alert type="error" showIcon title={errorMessage(error)} />;
  if (isPending) return <Skeleton active />;

  return (
    <>
      <PageHeader
        section="users"
        title={t("users.detailTitle", { id: data.username ? `@${data.username}` : data.id })}
        subtitle={data.first_name ?? String(data.telegram_id)}
        before={
          <Button
            icon={<ArrowLeftOutlined aria-hidden />}
            shape="circle"
            aria-label={t("common.back")}
            onClick={() => {
              void navigate(ROUTES.users);
            }}
          />
        }
        extra={
          <>
            <AdjustBalanceButton userId={userId} />
            <BlockUserButton userId={userId} blocked={data.is_blocked} />
            <MarkServiceUserButton userId={userId} service={data.is_service} />
            <ResetDemoButton userId={userId} />
          </>
        }
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <SectionCard
            color={SECTIONS.users.color}
            icon={<IdcardOutlined aria-hidden />}
            title={t("users.card")}
          >
            <Descriptions column={{ xs: 1, sm: 2, xl: 3 }} bordered>
              <Descriptions.Item label={t("users.balance")}>
                <Money value={data.balance} />
              </Descriptions.Item>
              <Descriptions.Item label={t("users.invested")}>
                <Money value={data.invested} />
              </Descriptions.Item>
              <Descriptions.Item label={t("users.earned")}>
                <Money value={data.earned_total} sign="always" colored />
              </Descriptions.Item>
              <Descriptions.Item label={t("common.status")}>
                {data.is_blocked ? <StatusTag status="blocked" /> : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="telegram_id">{data.telegram_id}</Descriptions.Item>
              <Descriptions.Item label={t("users.language")}>{data.language}</Descriptions.Item>
              <Descriptions.Item label={t("users.notifications")}>
                {data.notifications_enabled ? t("common.yes") : t("common.no")}
              </Descriptions.Item>
              <Descriptions.Item label={t("users.service")}>
                {data.is_service ? <Tag>{t("users.service")}</Tag> : "—"}
              </Descriptions.Item>
              {/* Только чтение: согласие даёт сам пользователь в настройках
                  профиля (§5). Согласие, выставленное оператором, — не
                  согласие, поэтому ручки, меняющей его, в админке нет вовсе.
                  Инструмент оператора здесь — пометка служебным. */}
              <Descriptions.Item label={t("users.publicRating")}>
                {data.public_rating_opt_in
                  ? t("users.publicRatingOn")
                  : t("users.publicRatingOff")}
              </Descriptions.Item>
              <Descriptions.Item label={t("users.registered")}>
                {formatDateTime(data.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label={t("users.referralCode")}>
                <Typography.Text copyable>{data.referral_code}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label={t("users.invitedCount")}>
                {data.invited_count}
              </Descriptions.Item>
              <Descriptions.Item label={t("users.referredBy")}>
                {data.referred_by ?? "—"}
              </Descriptions.Item>
            </Descriptions>
          </SectionCard>
        </Col>

        <Col span={24}>
          <RobotsCard user={data} />
        </Col>
        <Col span={24}>
          <TransactionsCard userId={userId} />
        </Col>
      </Row>
    </>
  );
}
